import React, { useEffect, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { adminNav } from '@/lib/navConfig';
import KPICard from '@/components/common/KPICard';
import { Wallet, ShoppingBag, Package, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api, { fmtIDR, currentMonth } from '@/lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [daily, setDaily] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [s, p, d] = await Promise.all([
        api.get(`/reports/sales-summary?month=${currentMonth()}`),
        api.get('/products?active_only=false'),
        api.get(`/reports/daily-sales?month=${currentMonth()}`),
      ]);
      setSummary(s.data);
      setProducts(p.data);
      setDaily(d.data);
    };
    load();
  }, []);

  const totalStock = products.reduce((a, p) => a + (p.stock || 0), 0);
  const lowStock = products.filter((p) => p.stock <= (p.low_stock_threshold || 5));

  return (
    <AppShell nav={adminNav} title="Dashboard Admin">
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard label="Omset Bulan Ini" value={fmtIDR(summary?.revenue || 0)} icon={Wallet} tone="info" testId="kpi-revenue" />
          <KPICard label="Transaksi" value={(summary?.transactions || 0).toLocaleString('id-ID')} icon={ShoppingBag} />
          <KPICard label="Total SKU" value={products.length} icon={Package} tone="warning" />
          <KPICard label="Stok Menipis" value={lowStock.length} icon={TrendingUp} tone={lowStock.length > 0 ? 'danger' : 'success'} />
        </div>

        <Card>
          <CardHeader><CardTitle>Penjualan Harian</CardTitle></CardHeader>
          <CardContent>
            {daily.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-sm text-muted-foreground">Belum ada data</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(8, 10)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1e6).toFixed(0)}jt`} />
                  <Tooltip formatter={(v) => fmtIDR(v)} />
                  <Bar dataKey="revenue" fill="hsl(186 86% 28%)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {lowStock.length > 0 && (
          <Card className="border-rose-200 bg-rose-50">
            <CardHeader><CardTitle className="text-rose-700">Stok Menipis ({lowStock.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {lowStock.slice(0, 12).map((p) => (
                  <div key={p.id} className="px-3 py-2 rounded-lg bg-white border text-sm">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground">Sisa {p.stock}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppShell>
  );
}
